import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Divider, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import CableIcon from '@mui/icons-material/Cable';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';

const ClientAsanaIntegration = ({ selectedClient, user }) => {
  const [connection, setConnection] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false); // NEW
  const [authUrl, setAuthUrl] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [ingestionData, setIngestionData] = useState(null); // Renamed for clarity
const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false); // NEW: State for the success modal

// Helper to format Firestore Timestamps
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Never';
  // Convert Firestore Timestamp to JavaScript Date and format it
  return new Date(timestamp.seconds * 1000).toLocaleString();
};

  const handleFetchData = async () => {
  setIsFetching(true);
  let toastId = toast.loading("Fetching project data from Asana...");
  try {
    const functions = getFunctions(undefined, 'us-central1');

    // Step 1: Fetch the data
    const getClientAsanaPortfolio = httpsCallable(functions, 'getClientAsanaPortfolio');
    const fetchResult = await getClientAsanaPortfolio({ clientId: selectedClient.id });

    if (!fetchResult.data.success) {
      throw new Error("Failed to fetch project data from Asana.");
    }

    const fetchedProjects = fetchResult.data.projects;
    toast.update(toastId, { render: `Fetched ${fetchedProjects?.length || 0} projects. Now saving...` });

     // Step 2: Save the data (using the correct function name after refactoring)
    const prepareAsanaDataForSanitization = httpsCallable(functions, 'prepareAsanaDataForSanitization');
    await prepareAsanaDataForSanitization({ 
      clientId: selectedClient.id 
    });

// Manually update the state for an immediate UI refresh
    setIngestionData({ ingestedAt: { seconds: Date.now() / 1000 } });
    toast.update(toastId, {
      render: `Asana data successfully ingested and saved.`,
      type: "success",
      isLoading: false,
      autoClose: 3000 // Shorten toast time
    });
    setIsSuccessModalOpen(true); // NEW: Open the success modal
    console.log("Saved Asana Projects:", fetchedProjects);
  } catch (error) {
    console.error("Error during Asana data ingestion:", error);
    toast.update(toastId, {
      render: `Error: ${error.message}`,
      type: "error",
      isLoading: false,
      autoClose: 5000
    });
  } finally {
    setIsFetching(false);
  }
};
  useEffect(() => {
    if (!selectedClient?.id) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setAuthUrl(null); // NEW: Reset auth URL when client changes
    const docPath = `clients/${selectedClient.id}/integrations/asana`;
    const unsub = onSnapshot(doc(db, docPath),
      (docSnap) => {
        if (docSnap.exists()) {
          setConnection(docSnap.data());
        } else {
          setConnection(null);
        }
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching client Asana connection:", error);
        toast.error("Failed to load client's Asana integration status.");
        setIsLoading(false);
      }
    );

    return () => unsub();
  }, [selectedClient?.id]);

     // NEW: Real-time listener for the ingested Asana data status
useEffect(() => {
  if (!selectedClient?.id) {
    setIngestionData(null);
    return;
  }

  const dataDocPath = `clients/${selectedClient.id}/asanaData/latestProjects`;
  const unsubData = onSnapshot(doc(db, dataDocPath),
    (docSnap) => {
      if (docSnap.exists()) {
        setIngestionData(docSnap.data());
      } else {
        setIngestionData(null);
      }
    },
    (error) => {
      console.error("Error fetching Asana ingestion data:", error);
      // Don't show a toast here to avoid cluttering the UI on load
    }
  );

  return () => unsubData();
}, [selectedClient?.id]);

  // MODIFIED: This function now fetches the URL and updates state instead of redirecting
  const handleConnect = async () => {
    setIsConnecting(true);
    const toastId = toast.loading("Generating secure connection link...");
    try {
      const functions = getFunctions(undefined, 'us-central1'); // Keep the region fix
      const initiateClientAsanaAuth = httpsCallable(functions, 'initiateClientAsanaAuth');
      const result = await initiateClientAsanaAuth({ clientId: selectedClient.id });

      if (result.data.authUrl) {
        toast.update(toastId, { render: "Link generated. Please proceed.", type: "success", isLoading: false, autoClose: 3000 });
        setAuthUrl(result.data.authUrl); // NEW: Set the URL in state
      } else {
        throw new Error("Could not retrieve Asana authorization URL.");
      }
    } catch (error) {
      console.error("Error initiating client Asana auth:", error);
      toast.update(toastId, { render: `Error: ${error.message}`, type: "error", isLoading: false, autoClose: 5000 });
    } finally {
      setIsConnecting(false);
    }
  };

  // NEW: Handler for the disconnect button
  const handleDisconnect = async () => {
    if (!window.confirm(`Are you sure you want to disconnect Asana for ${selectedClient.name}?`)) {
      return;
    }
    setIsDisconnecting(true);
    const toastId = toast.loading("Disconnecting client's Asana account...");
    try {
      const functions = getFunctions(undefined, 'us-central1');
      const disconnectClientAsana = httpsCallable(functions, 'disconnectClientAsana');
      await disconnectClientAsana({ clientId: selectedClient.id });
      toast.update(toastId, { render: "Client's Asana disconnected successfully.", type: "success", isLoading: false, autoClose: 5000 });
      // The onSnapshot listener will handle the UI update automatically.
    } catch (error) {
      console.error("Error disconnecting client Asana:", error);
      toast.update(toastId, { render: `Error: ${error.message}`, type: "error", isLoading: false, autoClose: 5000 });
    } finally {
      setIsDisconnecting(false);
    }
  };

  if (!selectedClient) {
    return null;
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
          <Typography sx={{ ml: 2 }}>Loading Asana Integration Status...</Typography>
        </Box>
      );
    }

    // NEW: Render the "Proceed" button if we have an authUrl
    if (authUrl) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 2, border: '1px dashed', borderColor: 'primary.main', borderRadius: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
              Ready to Connect
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Click below to go to Asana's secure website to authorize the connection for {selectedClient.name}.
            </Typography>
            <Button
              component="a" // This makes the button a hyperlink
              href={authUrl}  // The secure URL from our backend
              variant="contained"
              size="large"
            >
              Proceed to Asana
            </Button>
        </Box>
      );
    }

    if (connection?.status === 'connected') {
      // --- CONNECTED STATE ---
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CableIcon color="success" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  Asana Connected
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Connected by: {connection.asanaUserName || 'Unknown User'}
               </Typography>
               <Typography variant="body2" color="text.secondary">
               Last Ingested: {formatTimestamp(ingestionData?.ingestedAt)}
               </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
  <Button
    variant="contained"
    onClick={handleFetchData}
    disabled={isFetching}
    sx={{ textTransform: 'none', position: 'relative' }}
  >
    Fetch Data
    {isFetching && <CircularProgress size={24} sx={{ position: 'absolute' }} />}
  </Button>
  <Button
    variant="outlined"
    onClick={handleDisconnect}
    disabled={isDisconnecting}
    sx={{ textTransform: 'none', position: 'relative' }}
  >
    Disconnect
    {isDisconnecting && <CircularProgress size={24} sx={{ position: 'absolute' }} />}
  </Button>
</Box>
          </Box>
      );
    }
    
    // --- NOT CONNECTED STATE ---
    return (
       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CableIcon color="action" sx={{ fontSize: 40 }}/>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                Connect Client's Asana Account
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ingest this client's project data to enhance strategic insights.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={isConnecting}
            sx={{ textTransform: 'none', position: 'relative' }}
          >
            Connect
            {isConnecting && (
              <CircularProgress
                size={24}
                sx={{
                  color: 'primary.light',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Button>
        </Box>
    );
  };

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Client Asana Integration
      </Typography>
      <Divider sx={{ my: 2 }} />
      {renderContent()}
    <Dialog open={isSuccessModalOpen} onClose={() => setIsSuccessModalOpen(false)}>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <Typography>
            Asana data successfully ingested and saved. You will receive an email notifying you that the data is in the system ready to be used.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSuccessModalOpen(false)} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
export default ClientAsanaIntegration;